
import { defineComponent, watch } from "vue";
import JobModal from "@/components/JobModal.vue";
import SpeechSchedule from '@/components/SpeechSchedule.vue'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'
import RightToWorkCheckPopup from '@/components/RightToWorkCheckPopup.vue'
import { isAdmin, isBoss } from '@/utils/auth'

interface ScheduleViewModel {
  jobId: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime?: any;
  endTime?: any;
  jobDate: string;
  payRate?: number;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  statusInfo?: string;
  clientId?: number;
  clientInfo?: string;
  adjustmentHours?: number;
  bonusHours: number;
  travelExpense: number;
  remarks: string;
  rate: number;
  checkCallIntervalMinutes?: number | null;
  isCheckCallTrackingRequired?: boolean | null;
}

interface JobUpdateViewModel {
  jobId?: number;
  siteId?: number;
  siteInfo?: string;
  siteDetail?: string;
  startTime: string;
  endTime: string;
  jobDate: string;
  securityOfficerId?: number;
  securityOfficerInfo?: string;
  operator?: number;
  statusId?: number;
  clientId?: number;
  clientInfo?: string;
  remarks?: string;
  adjustment?: number;
  rate?: number;
  travelExpense?: number;
  payRate?: number;
  bonusHours?: number;
  oldJobId?: number;
  checkCallIntervalMinutes?: number | null;
  isCheckCallTrackingRequired?: boolean | null;
}

interface ScheduleViewModelDatewiseList {
  jobDate: string;
  scheduleViewModels: ScheduleViewModel[];
}

const __default__ = defineComponent({
  name: "ScheduleView",
  components: { JobModal , SpeechSchedule, Multiselect, RightToWorkCheckPopup },
  props: { baseUrl: String },

  data() {
    return {
      includeAdjustment: false,
      schedule: {} as Record<string, ScheduleViewModelDatewiseList>,
      jobEligibilityWarnings: {} as Record<number, string[]>,
      jobRightToWorkIssues: {} as Record<number, boolean>,
      rightToWorkOfficerId: null as number | null,
      dayList: [] as Array<{ dateKey: string; date: Date }>,
      hourList: [...Array(24).keys()],
      isAuthorized: false,
      isLoading: false,
      isCreatingJobs: false,
      isLoadingCompanies: false,

      selectedOfficerId: null,
      selectedCompanyId:null,
      selectedSiteId:null,
      isLoadingOfficers: false,
      startDate: new Date(),
      endDate: new Date(),

      companies: [] as Array<{ companyId: number; companyName: string }>,
      
      officers: [] as Array<any>,
      sites: [] as Array<any>,
      jobStatuses: [] as Array<any>,
      officerGroups: [] as Array<any>,
      selectedGroupIds: [] as Array<number>,

      // Drag and drop state
      dragSourceJob: null as ScheduleViewModel | null,
      dragSourceDay: null as { dateKey: string; date: Date } | null,
      dragSourceHour: null as number | null,
      isDragging: false,
      isDragOver: false,
      dragOverKey: null as string | null,
      dragTargetDate: null as string | null,
      dragTargetHour: null as number | null,
      dragSourceDate: null as string | null,
      jobsToCreate: 0,
      dateRangeCount: 0,

      // Copy / paste state
      copiedJob: null as ScheduleViewModel | null,
      contextMenu: {
        visible: false,
        x: 0,
        y: 0,
        day: null as { dateKey: string; date: Date } | null,
        hour: null as number | null,
        job: null as ScheduleViewModel | null,
      },

      // Delete feature properties
      selectMode: false,
      selectedJobIds: new Set<number>(),

      // Toast notification system
      showToast: false,
      toastMessage: "",
      toastType: "success" as "success" | "error" | "info" | "warning",
      toastTimeout: null as number | null,

      // Abort controller for canceling requests
      abortController: null as AbortController | null,
    };
  },

  computed: {
    hasAnyJobs(): boolean {
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels?.length > 0) {
          return true;
        }
      }
      return false;
    },
    
    hasScheduleData(): boolean {
      return Object.keys(this.schedule).length > 0;
    },

    toastIcon(): string {
      const icons: Record<string, string> = {
        success: "✅",
        error: "❌",
        warning: "⚠️",
        info: "ℹ️"
      };
      return icons[this.toastType] || "ℹ️";
    },

    jobSequenceMap(): Record<number, number> {
      const map: Record<number, number> = {};
      let counter = 1;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          const jobs = this.jobsForSlot(day, hour);
          for (const job of jobs) {
            map[job.jobId] = counter++;
          }
        }
      }
      return map;
    },
    
    dailyTotalHours(): Record<string, string> {
      const totals: Record<string, string> = {};
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        let totalMinutes = 0;
        
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
        
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const roundedMinutes = Math.round(minutes * 100) / 100;
        
        totals[day.dateKey] = roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
      }
      return totals;
    },

    grandTotalHours(): string {
      let totalMinutes = 0;
      
      for (const day of this.dayList) {
        const dayData = this.schedule[day.dateKey];
        if (dayData?.scheduleViewModels) {
          for (const job of dayData.scheduleViewModels) {
            totalMinutes += this.getDurationInMinutes(job.startTime, job.endTime, job.adjustmentHours);
          }
        }
      }
      
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const roundedMinutes = Math.round(minutes * 100) / 100;
      
      return roundedMinutes === 0 ? `${hours}h` : `${hours}h ${roundedMinutes}m`;
    },

    firstNonEmptyHour(): number {
      let earliestHour = 24;
      for (const day of this.dayList) {
        for (let hour = 0; hour < 24; hour++) {
          if (this.jobsForSlot(day, hour).length > 0 && hour < earliestHour) {
            earliestHour = hour;
          }
        }
      }
      return earliestHour === 24 ? 6 : earliestHour;
    },

    filteredHourList(): number[] {
      return this.hourList.filter(hour => {
        if (hour < this.firstNonEmptyHour) return false;
        return this.dayList.some(day => this.jobsForSlot(day, hour).length > 0);
      });
    },

    startDateInput(): string {
      return this.dateToYYYYMMDD(this.startDate);
    },
    endDateInput(): string {
      return this.dateToYYYYMMDD(this.endDate);
    },
  },

  watch: {
    selectedCompanyId() { this.getScheduleForToday(); },
    includeAdjustment() { this.getScheduleForToday(); },
    selectedOfficerId() { this.getScheduleForToday(); }, // Add this
    selectedSiteId() { this.getScheduleForToday(); }, // Add this
  },

  methods: {

    exportToExcel() {
      // Get all jobs from current schedule data
      const allJobs = [];
      for (const [dateKey, dayData] of Object.entries(this.schedule)) {
        // Skip if date doesn't match current date range
        const jobDate = new Date(dateKey);
        const startDate = new Date(this.startDate);
        const endDate = new Date(this.endDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);
        jobDate.setHours(0, 0, 0, 0);
        
        if (jobDate < startDate || jobDate > endDate) continue;
        
        if (dayData.scheduleViewModels) {
          dayData.scheduleViewModels.forEach((job: any) => {
            let badgeNumber = job.siaBadgeNumber || '';
            if (badgeNumber) {
              badgeNumber = badgeNumber.toString().trim();
              //badgeNumber = "'" + badgeNumber;
            }
            
            let mobileNumber = job.mobileNumber || '';
            if (mobileNumber) {
              mobileNumber = mobileNumber.toString().trim();
              //mobileNumber = "'" + mobileNumber;
            }
            
            allJobs.push({
              Date: job.jobDate ? new Date(job.jobDate).toLocaleDateString() : '',
              Time: (job.startTime ? job.startTime.slice(0,5) : '') + " - " + (job.endTime ? job.endTime.slice(0,5) : ''),
              Site: job.siteDetail || '',
              Officer: job.securityOfficerInfo || '',
              Mobile: mobileNumber,
              Badge: badgeNumber,
            });
          });
        }
      }

      if (allJobs.length === 0) {
        this.showNotification("No jobs to export.", "warning");
        return;
      }

      let html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" 
              xmlns:x="urn:schemas-microsoft-com:office:excel" 
              xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta charset="UTF-8">
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Schedule</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              font-family: Arial, sans-serif;
              font-size: 18px;
            }
            th { 
              background-color: #0000ff; 
              color: white; 
              font-weight: bold;
              mso-background-color: #0000ff; /* Added for Excel compatibility */
              text-align: center;
            }
            tr:nth-child(even) { background-color: #f2f2f2; }
            .number { mso-number-format: "\\@"; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th  style="text-align:center;background-color:lightblue;">Date</th>
                <th  style="background-color:lightblue;text-align:center;">Time</th>
                <th  style="background-color:lightblue;">Site</th>
                <th  style="background-color:lightblue;">Officer</th>
                <th  style="background-color:lightblue;">Mobile</th>
                <th  style="background-color:lightblue;">SIA Badge</th>
              </tr>
            </thead>
            <tbody>
      `;

      allJobs.forEach(row => {
        html += `
          <tr>
            <td>${row.Date}</td>
            <td>${row.Time}</td>
            <td>${row.Site}</td>
            <td>${row.Officer}</td>
            <td class="number">${row.Mobile}</td>
            <td class="number">${row.Badge}</td>
          </tr>
        `;
      });

      html += `
            </tbody>
          </table>
        </body>
        </html>
      `;

      const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Schedule_${new Date().toISOString().split('T')[0]}.xls`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.showNotification(`${allJobs.length} jobs exported!`, "success");
    },
    updateStartDate(event: any) {
      if (event.target.value) {
        const date = new Date(event.target.value);
        date.setHours(0, 0, 0, 0);
        this.startDate = date;
        this.getScheduleForToday();
      }
    },
    
    updateEndDate(event: any) {
      if (event.target.value) {
        const date = new Date(event.target.value);
        date.setHours(0, 0, 0, 0);
        this.endDate = date;
        this.getScheduleForToday();
      }
    },

    onScheduleLoaded(data: any) {
      // Process the schedule data from speech
      if (data && data.schedule) {
        this.schedule = data.schedule;
        this.dayList = data.dates || [];
      }
      
      // Update startDate and endDate from speech
      if (data && data.startDate && data.endDate) {
        const start = new Date(data.startDate);
        const end = new Date(data.endDate);
        
        if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
          // Set proper time components
          start.setHours(0, 0, 0, 0);
          end.setHours(0, 0, 0, 0);
          
          // Update the reactive properties
          this.startDate = new Date(start);
          this.endDate = new Date(end);
          
          // Force Vue to re-render the date inputs
          this.$forceUpdate();
          
          // Refresh the schedule with the new dates
          this.$nextTick(() => {
            this.getScheduleForToday();
          });
        }
      }
    },

    showNotification(message: string, type: "success" | "error" | "info" | "warning" = "info") {
      this.toastMessage = message;
      this.toastType = type;
      this.showToast = true;
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => this.closeToast(), 5000);
    },

    closeToast() {
      this.showToast = false;
      if (this.toastTimeout) {
        clearTimeout(this.toastTimeout);
        this.toastTimeout = null;
      }
    },

    hasJobConflict(day: { dateKey: string; date: Date }, hour: number): boolean {
      if (!this.dragSourceJob || !this.dragSourceDay) return false;
      const existingJobs = this.jobsForSlot(day, hour);
      return existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
    },

    toggleSelectMode() {
      this.selectMode = !this.selectMode;
      if (!this.selectMode) this.selectedJobIds.clear();
    },

    toggleJobSelection(jobId: number) {
      this.selectedJobIds.has(jobId) 
        ? this.selectedJobIds.delete(jobId) 
        : this.selectedJobIds.add(jobId);
    },

    async deleteSelectedJobs() {
      if (this.selectedJobIds.size === 0) {
        this.showNotification("No jobs selected for deletion.", "warning");
        return;
      }
      
      if (!confirm(`Are you sure you want to delete ${this.selectedJobIds.size} job(s)?\n\nThis action cannot be undone.`)) {
        return;
      }
      
      try {
        const jobIdsArray = Array.from(this.selectedJobIds).map(id => ({ Id: id }));
        const response = await fetch(this.baseUrl + "schedule/DeleteJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobIdsArray)
        });
        
        if (response.ok) {
          this.selectedJobIds.clear();
          this.selectMode = false;
          await this.getScheduleForToday();
          this.showNotification(`${jobIdsArray.length} job(s) deleted successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to delete jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error deleting jobs:", error);
        this.showNotification("Failed to delete jobs. Please try again.", "error");
      }
    },

    getTimeTooltip(duty: ScheduleViewModel) {
      const date = duty.jobDate ? new Date(duty.jobDate).toLocaleDateString() : 'Unknown date';
      return `${date} ${this.formatTimeSpan(duty.startTime)}–${this.formatTimeSpan(duty.endTime)}`;
    },

    getAllLookupData() {
      this.isLoadingCompanies = true;
      
      try {
        // Read from localStorage (cached by login)
        const cachedData = localStorage.getItem("lookupData");
        
        if (!cachedData) {
          this.showNotification("Please login again to load data.", "warning");
          this.isAuthorized = false;
          this.isLoadingCompanies = false;
          return;
        }
        
        const data = JSON.parse(cachedData);
        
        this.officers = data.officers || [];
        this.sites = data.sites || [];
        this.officerGroups = data.officerGroups || [];
        this.jobStatuses = data.jobStatuses || [];
        
        let companiesData = data.companies || [];
        if (companiesData.data && Array.isArray(companiesData.data)) {
          companiesData = companiesData.data;
        }
        if (companiesData.values && Array.isArray(companiesData.values)) {
          companiesData = companiesData.values;
        }
        
        this.companies = (Array.isArray(companiesData) ? companiesData : []).map((company: any) => ({
          companyId: company.companyId || company.id || company.Id || company.companyID || 0,
          companyName: company.companyName || company.name || company.Name || company.companyName || 'Unknown'
        }));
        
        this.isAuthorized = true;
        
      } catch (error) {
        console.error("Error loading lookup data:", error);
        this.showNotification("Failed to load client data.", "error");
      } finally {
        this.isLoadingCompanies = false;
      }
    },

    parseTimeToMinutes(timeValue: any): number {
      if (!timeValue) return 0;
      if (typeof timeValue === 'string') {
        const parts = timeValue.split(':');
        return (parseInt(parts[0]) || 0) * 60 + (parseInt(parts[1] || '0') || 0);
      }
      if (typeof timeValue === 'object') {
        return (timeValue.Hours || 0) * 60 + (timeValue.Minutes || 0);
      }
      return 0;
    },
    
    formatDayLabel(date: Date) {
      if (!date || isNaN(date.getTime())) return "Invalid";
      return date.toLocaleDateString("en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short"
      });
    },

    formatHourLabel(hour: number) {
      return `${hour.toString().padStart(2, "0")}:00`;
    },

    formatTimeSpan(timeSpan: any) {
      if (!timeSpan) return "N/A";
      if (typeof timeSpan === 'string') return timeSpan.substring(0, 5);
      if (typeof timeSpan === 'object') {
        const hours = timeSpan.Hours?.toString().padStart(2, '0') || '00';
        const minutes = timeSpan.Minutes?.toString().padStart(2, '0') || '00';
        return `${hours}:${minutes}`;
      }
      return "N/A";
    },

    formatOfficerName(name: string, dayCount: number) {
      if (!name) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      if (name.length <= maxLength) return name;
      
      const parts = name.trim().split(' ');
      if (parts.length === 1) return parts[0].substring(0, maxLength - 2) + '..';
      return `${parts[0]} ${parts[1]?.charAt(0)}.`;
    },

    formatSiteName(site: string, dayCount: number) {
      if (!site) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 25 : 15;
      const fullName = site.split(' - ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    formatClientName(client: string, dayCount: number) {
      if (!client) return "N/A";
      const maxLength = dayCount <= 3 ? 100 : dayCount <= 5 ? 20 : 10;
      const fullName = client.split(' ')[0];
      return fullName.length <= maxLength ? fullName : fullName.substring(0, maxLength - 2) + '..';
    },

    jobsForSlot(day: { dateKey: string; date: Date }, hour: number) {
      if (!day?.date) return [];
      const dayData = this.schedule[day.dateKey];
      if (!dayData?.scheduleViewModels) return [];
      
      return dayData.scheduleViewModels.filter((duty: ScheduleViewModel) => {
        if (!duty.startTime) return false;
        
        if (duty.jobDate) {
          const jobDateObj = new Date(duty.jobDate);
          const columnDateObj = new Date(day.date);
          if (jobDateObj.toDateString() !== columnDateObj.toDateString()) return false;
        }
        
        let startHour = 0;
        if (typeof duty.startTime === 'string') startHour = parseInt(duty.startTime.substring(0, 2));
        else if (typeof duty.startTime === 'object' && duty.startTime.Hours !== undefined) {
          startHour = duty.startTime.Hours;
        }
        return startHour === hour;
      });
    },

    getDateKey(date: Date): string {
      // Ensure we're using the date without time
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00`;
    },

    setStatusColor(statusId: any) {
      return {
        'status-completed': statusId == 2,
        'status-in-progress': statusId == 4,
        'status-pending': statusId == 3,
        'status-cancelled': statusId == 8
      };
    },

    dateToYYYYMMDD(d: Date | string) {
      if (!d) return "";
      const dateObj = typeof d === 'string' ? new Date(d) : d;
      if (isNaN(dateObj.getTime())) return "";
      
      // Use local date components to avoid timezone issues
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    calculateJobDuration(startTime: any, endTime: any): string {
      if (!startTime || !endTime) return "0h";
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let durationMinutes = endTotalMinutes - startTotalMinutes;
      if (durationMinutes < 0) durationMinutes = 1440 + durationMinutes;
      
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
    },

    getDurationInMinutes(startTime: any, endTime: any, adjustmentHours = 0): number {
      if (!startTime || !endTime) return 0;
      
      const startTotalMinutes = this.parseTimeToMinutes(startTime);
      const endTotalMinutes = this.parseTimeToMinutes(endTime);
      
      let duration = endTotalMinutes - startTotalMinutes;
      if (duration < 0) duration = 1440 + duration;
      if (this.includeAdjustment) duration += (adjustmentHours || 0) * 60;
      
      return duration;
    },


    getScheduleForToday() {
      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();
      this.isLoading = true;
      
      let companyId = 0;
      if (this.selectedCompanyId) {
        const parsed = parseInt(this.selectedCompanyId);
        if (!isNaN(parsed) && parsed > 0) companyId = parsed;
      }

      // FIXED: Ensure dates are properly normalized
      const startDate = new Date(this.startDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(this.endDate);
      endDate.setHours(23, 59, 59, 999);

      console.log('Fetching schedule from:', startDate.toISOString(), 'to:', endDate.toISOString());

      // Build request body - FIXED: Use this. for the variables
      const requestBody: any = {
        StartDate: startDate,
        EndDate: endDate,
        PageNumber: 0,
        RecordsPerPage: 5000,
        SearchString: "",
        CompanyId: companyId
      };

      // Only add OfficerId if not null (using this.)
      if (this.selectedCompanyId) {
        requestBody.CompanyId = this.selectedCompanyId;
      }

      // Only add OfficerId if not null (using this.)
      if (this.selectedOfficerId) {
        requestBody.OfficerId = this.selectedOfficerId;
      }


      // Only add SiteId if not null (using this.)
      if (this.selectedSiteId) {
        requestBody.SiteId = this.selectedSiteId;
      }

      fetch(this.baseUrl + "schedule/ScheduleDictionaryForDateRangePageWise", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody),
        signal: this.abortController.signal
      })
      .then(r => {
        if (r.status === 401) {
          this.isAuthorized = false;
          this.isLoading = false;
          this.showNotification("Session expired. Please login again.", "warning");
          return null;
        }
        this.isAuthorized = true;
        return r.json();
      })
      .then(result => {
        this.isLoading = false;
        if (!result) return;

        const scheduleData = result.scheduleSinglePageResult;
        if (!scheduleData) {
          this.schedule = {};
          this.dayList = [];
          return;
        }

        this.schedule = scheduleData;

        // FIXED: Build day list with proper date handling
        const dayListArray: Array<{ dateKey: string; date: Date }> = [];
        const currentDate = new Date(this.startDate);
        const endDateObj = new Date(this.endDate);
        
        // Normalize dates
        currentDate.setHours(0, 0, 0, 0);
        endDateObj.setHours(0, 0, 0, 0);

        // Include the end date by using <=
        while (currentDate <= endDateObj) {
          const dateKey = this.getDateKey(currentDate);
          dayListArray.push({
            dateKey: dateKey,
            date: new Date(currentDate)
          });
          currentDate.setDate(currentDate.getDate() + 1);
        }

        // Sort the day list
        this.dayList = dayListArray.sort((a, b) => a.date.getTime() - b.date.getTime());

        this.checkOfficerEligibilityBulk();
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        this.isLoading = false;
        console.error("Error fetching schedule:", error);
        this.showNotification("Failed to load schedule data.", "error");
      });
    },

    checkOfficerEligibilityBulk() {
      const jobs: any[] = [];
      for (const dateKey of Object.keys(this.schedule)) {
        const dayData = this.schedule[dateKey];
        for (const duty of dayData?.scheduleViewModels || []) {
          if (!duty.securityOfficerId) continue; // TBC/unassigned jobs have nothing to check
          jobs.push({
            jobId: duty.jobId,
            securityOfficerId: duty.securityOfficerId,
            siteId: duty.siteId || null,
            clientId: duty.clientId || null,
            jobDate: duty.jobDate,
            startTime: this.formatTimeSpan(duty.startTime),
            endTime: this.formatTimeSpan(duty.endTime)
          });
        }
      }

      if (jobs.length === 0) {
        this.jobEligibilityWarnings = {};
        this.jobRightToWorkIssues = {};
        return;
      }

      fetch(this.baseUrl + "schedule/CheckOfficersForJobsBulk", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ jobs })
      })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(result => {
        const map: Record<number, string[]> = {};
        const rightToWorkMap: Record<number, boolean> = {};
        for (const item of result?.results || []) {
          if (item.hasWarnings) map[item.jobId] = item.warnings;
          if (item.hasRightToWorkIssue) rightToWorkMap[item.jobId] = true;
        }
        this.jobEligibilityWarnings = map;
        this.jobRightToWorkIssues = rightToWorkMap;
      })
      .catch(error => {
        console.error("Error checking officer eligibility (bulk):", error);
        // Progressive enhancement only — the grid must keep working without this.
      });
    },

    hasEligibilityIssue(jobId: number): boolean {
      return !!this.jobEligibilityWarnings[jobId]?.length;
    },

    getEligibilityTooltip(duty: ScheduleViewModel): string {
      const text = this.jobEligibilityWarnings[duty.jobId]?.join('\n') || '';
      return this.canOpenRightToWork(duty) ? `${text}\n\nClick to check right to work` : text;
    },

    // Only admin/Boss may run the check (same roles as the RightToWork API)
    canOpenRightToWork(duty: ScheduleViewModel): boolean {
      return !!this.jobRightToWorkIssues[duty.jobId] && !!duty.securityOfficerId && (isAdmin() || isBoss());
    },

    // Right-to-work warning: open the check directly. Anything else: behave like clicking the job.
    onEligibilityIconClick(duty: ScheduleViewModel) {
      if (this.selectMode) {
        this.toggleJobSelection(duty.jobId);
      } else if (this.canOpenRightToWork(duty)) {
        this.rightToWorkOfficerId = duty.securityOfficerId!;
      } else {
        this.openEditJob(duty);
      }
    },

    OnAddJobCliked() {
      (this.$refs.jobModal as any).openForAdd();
    },

    isJobLocked(duty: ScheduleViewModel): boolean {
      if (isBoss()) {
        return false;
      }
      const jobDate = new Date(duty.jobDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      jobDate.setHours(0, 0, 0, 0);
      return jobDate.getTime() < today.getTime();
    },

    openEditJob(duty: ScheduleViewModel) {
      (this.$refs.jobModal as any).openForEdit(duty, this.isJobLocked(duty));
    },

    onDragStart(event: DragEvent, duty: ScheduleViewModel, day: { dateKey: string; date: Date }, hour: number) {
      this.dragSourceJob = duty;
      this.dragSourceDay = day;
      this.dragSourceHour = hour;
      this.isDragging = true;
      this.dragSourceDate = this.formatDayLabel(day.date);
      
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'copy';
        event.dataTransfer.setData('text/plain', JSON.stringify({
          jobId: duty.jobId,
          dateKey: day.dateKey,
          hour: hour
        }));
      }
      event.stopPropagation();
    },

    onDragEnd() {
      this.dragSourceJob = null;
      this.dragSourceDay = null;
      this.dragSourceHour = null;
      this.isDragging = false;
      this.isDragOver = false;
      this.dragOverKey = null;
      this.dragTargetDate = null;
      this.dragTargetHour = null;
      this.dragSourceDate = null;
      this.dateRangeCount = 0;
      this.jobsToCreate = 0;
    },

    onDragOver(day: { dateKey: string; date: Date }, hour: number) {
      this.isDragOver = true;
      this.dragOverKey = `${day.dateKey}-${hour}`;
      this.dragTargetDate = this.formatDayLabel(day.date);
      this.dragTargetHour = hour;
      
      if (this.dragSourceDay) {
        const startDate = new Date(this.dragSourceDay.date);
        const endDate = new Date(day.date);
        const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        this.dateRangeCount = diffDays + 1;
        this.jobsToCreate = this.dateRangeCount;
      }
    },

    onDragLeave() {
      this.isDragOver = false;
      this.dragOverKey = null;
    },

    async onDrop(day: { dateKey: string; date: Date }, targetHour: number) {
      this.isDragOver = false;
      
      if (!this.dragSourceJob || !this.dragSourceDay) {
        this.onDragEnd();
        return;
      }
      
      const existingJobs = this.jobsForSlot(day, targetHour);
      const hasConflict = existingJobs.some(job => job.jobId !== this.dragSourceJob?.jobId);
      
      if (hasConflict) {
        if (!confirm(`There are already ${existingJobs.length} job(s) at this time. Create additional jobs anyway?`)) {
          this.onDragEnd();
          return;
        }
      }
      
      const isSameDay = this.dragSourceDay.dateKey === day.dateKey;
      const isSameHour = this.dragSourceHour === targetHour;
      
      const startDate = new Date(this.dragSourceDay.date);
      const endDate = new Date(day.date);
      const fromDate = startDate <= endDate ? startDate : endDate;
      const toDate = startDate <= endDate ? endDate : startDate;
      
      const dateRange: Date[] = [];
      const currentDate = new Date(fromDate);
      while (currentDate <= toDate) {
        dateRange.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      let datesToCreate: Date[] = [];
      const sourceDateStr = this.dateToYYYYMMDD(this.dragSourceDay.date);
      
      if (isSameDay) {
        datesToCreate = [new Date(day.date)];
      } else {
        datesToCreate = dateRange.filter(date => this.dateToYYYYMMDD(date) !== sourceDateStr);
      }
      
      if (datesToCreate.length === 0) {
        this.showNotification("No new jobs to create.", "info");
        this.onDragEnd();
        return;
      }
      
      const durationMinutes = this.getDurationInMinutes(
        this.dragSourceJob.startTime, 
        this.dragSourceJob.endTime
      );
      
      const newStartTime = this.formatTimeString(targetHour, 0);
      const newEndTime = this.formatTimeString(
        targetHour + Math.floor(durationMinutes / 60),
        durationMinutes % 60
      );

      const currentUserId = this.getCurrentUserId();

      let confirmMessage = "";
      if (isSameDay) {
        if (isSameHour) {
          confirmMessage = `Create a DUPLICATE job at the SAME time (${targetHour}:00) on ${this.formatDayLabel(day.date)}?\n\nOriginal job will remain unchanged.`;
        } else {
          confirmMessage = `Create a NEW job at ${targetHour}:00 on the SAME DAY (${this.formatDayLabel(day.date)})?\n\nOriginal job at ${this.dragSourceHour}:00 will remain unchanged.`;
        }
      } else {
        confirmMessage = `Create ${datesToCreate.length} job(s) for "${this.dragSourceJob.securityOfficerInfo}"\nFrom: ${this.formatDayLabel(fromDate)} at ${this.dragSourceHour}:00\nTo: ${this.formatDayLabel(toDate)} at ${targetHour}:00?\n\nThe original job on ${this.formatDayLabel(this.dragSourceDay.date)} will remain unchanged.`;
      }
      
      if (!confirm(confirmMessage)) {
        this.onDragEnd();
        return;
      }
      
      const sourceJob = this.dragSourceJob;
      const jobsToCreateList = datesToCreate.map(date => this.buildCopiedJob(sourceJob, date, currentUserId));
      try {
        await this.postNewJobs(jobsToCreateList);
      } finally {
        this.onDragEnd();
      }
    },

    getCurrentUserId(): number | undefined {
      let currentUserId: number | undefined;
      try {
        const token = localStorage.getItem("token");
        if (token) {
          const tokenPayload = JSON.parse(atob(token.split('.')[1]));
          const userId = tokenPayload.nameid || tokenPayload.userId || tokenPayload.sub;
          if (userId) {
            currentUserId = typeof userId === 'number' ? userId : parseInt(userId);
            if (isNaN(currentUserId as number)) currentUserId = undefined;
          }
        }
      } catch (e) {
        console.warn("Could not decode token:", e);
      }

      if (!currentUserId) {
        const userIdFromStorage = localStorage.getItem("userId");
        if (userIdFromStorage) {
          currentUserId = parseInt(userIdFromStorage);
          if (isNaN(currentUserId)) currentUserId = undefined;
        }
      }
      return currentUserId;
    },

    buildCopiedJob(source: ScheduleViewModel, date: Date, operator?: number): JobUpdateViewModel {
      return {
        siteId: source.siteId,
        siteInfo: source.siteInfo,
        siteDetail: source.siteDetail,
        startTime: source.startTime,
        endTime: source.endTime,
        jobDate: this.dateToYYYYMMDD(date),
        securityOfficerId: source.securityOfficerId,
        securityOfficerInfo: source.securityOfficerInfo,
        operator,
        statusId: source.statusId,
        clientId: source.clientId,
        clientInfo: source.clientInfo,
        remarks: source.remarks,
        adjustment: source.adjustmentHours,
        rate: source.rate,
        travelExpense: source.travelExpense,
        bonusHours: source.bonusHours,
        payRate: source.payRate,
        oldJobId: undefined,
        checkCallIntervalMinutes: source.checkCallIntervalMinutes,
        isCheckCallTrackingRequired: source.isCheckCallTrackingRequired
      };
    },

    async postNewJobs(jobsToCreateList: JobUpdateViewModel[]) {
      this.isCreatingJobs = true;
      this.jobsToCreate = jobsToCreateList.length;

      try {
        const response = await fetch(this.baseUrl + "schedule/AddJobs", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify(jobsToCreateList)
        });

        if (response.ok) {
          await this.getScheduleForToday();
          this.showNotification(`${jobsToCreateList.length} job(s) created successfully!`, "success");
        } else {
          const errorText = await response.text();
          throw new Error(`Failed to create jobs: ${errorText}`);
        }
      } catch (error) {
        console.error("Error creating jobs:", error);
        this.showNotification("Failed to create jobs. Please try again.", "error");
      } finally {
        this.isCreatingJobs = false;
      }
    },

    openContextMenu(event: MouseEvent, day: { dateKey: string; date: Date }, hour: number, job: ScheduleViewModel | null) {
      // Fall back to the browser's own menu in select mode or when there is nothing to offer
      if (this.selectMode || (!job && !this.copiedJob)) {
        this.closeContextMenu();
        return;
      }
      event.preventDefault();
      this.contextMenu = { visible: true, x: event.clientX, y: event.clientY, day, hour, job };
    },

    closeContextMenu() {
      this.contextMenu.visible = false;
    },

    onGlobalKeydown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (this.contextMenu.visible) {
        this.closeContextMenu();
      } else if (this.copiedJob) {
        this.clearCopiedJob();
      }
    },

    copyJob(job: ScheduleViewModel) {
      this.copiedJob = { ...job };
      this.closeContextMenu();
      this.showNotification("Job copied. Right-click on any date to paste.", "info");
    },

    clearCopiedJob() {
      this.copiedJob = null;
      this.closeContextMenu();
    },

    async pasteJob(day: { dateKey: string; date: Date }, hour: number) {
      const source = this.copiedJob;
      this.closeContextMenu();
      if (!source) return;

      const existingJobs = this.jobsForSlot(day, hour);
      if (existingJobs.length > 0) {
        if (!confirm(`There are already ${existingJobs.length} job(s) at this time. Create additional jobs anyway?`)) {
          return;
        }
      }

      const timeRange = `${this.formatTimeSpan(source.startTime)}–${this.formatTimeSpan(source.endTime)}`;
      if (!confirm(`Paste job for "${source.securityOfficerInfo}" on ${this.formatDayLabel(day.date)} (${timeRange})?`)) {
        return;
      }

      // Paste creates exactly one job, on the clicked date only.
      await this.postNewJobs([this.buildCopiedJob(source, day.date, this.getCurrentUserId())]);
    },

    formatTimeString(hours: number, minutes: number): string {
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
    },
  },

  mounted() {
    this.isAuthorized = !!localStorage.getItem("token");
    this.getAllLookupData();
    this.getScheduleForToday();
    window.addEventListener('lookup-data-changed', this.getAllLookupData);
    document.addEventListener('click', this.closeContextMenu);
    window.addEventListener('scroll', this.closeContextMenu, true);
    window.addEventListener('keydown', this.onGlobalKeydown);
  },

  beforeUnmount() {
    if (this.abortController) this.abortController.abort();
    if (this.toastTimeout) clearTimeout(this.toastTimeout);
    window.removeEventListener('lookup-data-changed', this.getAllLookupData);
    document.removeEventListener('click', this.closeContextMenu);
    window.removeEventListener('scroll', this.closeContextMenu, true);
    window.removeEventListener('keydown', this.onGlobalKeydown);
  }
});

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "bb509e04": (_ctx.dayList.length)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__